"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlatformError } from "./client";
import { platformFetch, platformSend } from "./browser";
import { ADD_WALLET_STATEMENT, SIGN_IN_STATEMENT, signInMessage } from "@/lib/wallet/message";
import { connectWallet, signWalletMessage } from "@/lib/wallet/sign";
import { signInWithReferral } from "./referral-intake";
import type { SignInMethods } from "./signin-methods";
import type { DetectedWallet } from "@/lib/wallet/types";

export type { SignInMethods } from "./signin-methods";

/* The session, from the browser's side.

   There is no token to store: the platform sets an httpOnly cookie and the
   proxy rewrites it onto our origin, so "am I signed in" is just whether
   /whoami answers. A 401 is the signed-out state, not an error. */

export type Identity = {
  id: string;
  nickname: string;
  hasChangedNickname: boolean;
  isHidden: boolean;
  /* Where the player stands on KYC (lib/api/kyc.ts). Optional: a platform
     that predates them leaves them out, and the KYC interface stays hidden. */
  kycEnabled?: boolean;
  kycLevel?: number;
  requiredKycLevel?: number;
  pendingVerification?: boolean;
};

export const useIdentity = () => {
  const query = useQuery({
    queryKey: ["whoami"],
    queryFn: async () => {
      try {
        return (await platformFetch<{ data: Identity }>("whoami")).data;
      } catch (error) {
        if (error instanceof PlatformError && error.kind === "unauthorized") {
          return null;
        }

        throw error;
      }
    },
    staleTime: 30_000,
  });

  return {
    ...query,
    identity: query.data ?? null,
    isSignedIn: query.data !== null && query.data !== undefined,
  };
};

export const useBalances = (enabled: boolean) =>
  useQuery({
    queryKey: ["balances"],
    enabled,
    staleTime: 10_000,
    /* The whitelabel polls the balance at 5s — a stale figure after a spin is
       the most visible kind of wrong. Socket transaction events refetch it
       sooner still. */
    refetchInterval: enabled ? 5_000 : false,
    queryFn: async () =>
      (
        await platformFetch<{
          data: {
            amount: string;
            trancheLockedAmount: string;
            amlLockedAmount: string;
            bankroll: { id: string; token: { symbol: string; decimals: number; significantDecimals: number } };
          }[];
        }>("balances")
      ).data,
  });

export type Site = {
  id: string;
  name: string;
  policies: { requireEmailVerificationForWithdrawal: boolean };
  /* Not served by the public API yet. The platform's internal site records
     already model these — url, label and positions like "footer:platform",
     "footer:legal", "footer:socials" — and the whitelabel's footer reads them
     from its private site service. The moment the public /site starts carrying
     them, the footer below prefers them over the admin columns; until then the
     field is simply absent. */
  links?: { url: string; label: string; positions: string[] }[];
};

export const useSite = () =>
  useQuery({
    queryKey: ["site"],
    staleTime: 60 * 60_000,
    queryFn: async () => (await platformFetch<{ data: Site }>("site")).data,
  });

export const useSignInMethods = () =>
  useQuery({
    queryKey: ["signin", "methods"],
    staleTime: 5 * 60_000,
    queryFn: async () => (await platformFetch<{ data: SignInMethods }>("signin")).data,
  });

/* The magic link is the shortest path to a session: an email in, a link out.
   It is also the flow that fails loudest when our origin is not allowlisted,
   which is why the error is surfaced verbatim rather than swallowed. */
export const useMagicLink = () =>
  useMutation({
    /* The referral captured at landing by useReferralCapture. The platform
       records it when the link is created, so this is the moment it must be
       attached — the verify step never sees it. A click id another account
       already claimed is answered 409, and the link is asked for again
       without it (referral-intake.ts). */
    mutationFn: (email: string) =>
      signInWithReferral(
        async (referral) =>
          (
            await platformSend<{ data: { expiresAt: string } }>("signin/magic-link", {
              email,
              ...referral,
              /* Where the player lands *after* the token is exchanged, which is
                 not where the emailed link points — the platform builds that
                 itself as /signin/magic-link/verify. Sending them back to the
                 verify page would show "This link is incomplete", an error at
                 the end of a sign-in that worked. */
              redirect_url: `${window.location.origin}/`,
            })
          ).data,
      ),
  });

export const useSignOut = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => platformSend<unknown>("signout", {}),
    onSettled: () => {
      client.resetQueries();
    },
  });
};

export const useWalletSignIn = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (wallet: DetectedWallet) => {
      const scheme = wallet.chain === "evm" ? "siwe" : "siws";
      const connection = await connectWallet(wallet);
      const { data: challenge } = await platformFetch<{ data: { nonce: string } }>(
        `signin/${scheme}/nonce`,
      );

      const message = signInMessage({
        chain: wallet.chain,
        domain: window.location.host,
        address: connection.address,
        uri: window.location.origin,
        statement: SIGN_IN_STATEMENT,
        chainId: connection.chainId,
        nonce: challenge.nonce,
        issuedAt: new Date().toISOString(),
      });

      const signature = await signWalletMessage(wallet, connection.address, message);

      return signInWithReferral(
        async (referral) =>
          (
            await platformSend<{ data: { id: string; nickname: string } }>(`signin/${scheme}/verify`, {
              message,
              signature,
              ...referral,
            })
          ).data,
      );
    },
    onSuccess: () => {
      client.resetQueries();
    },
  });
};

export const useLinkWallet = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (wallet: DetectedWallet) => {
      const scheme = wallet.chain === "evm" ? "siwe" : "siws";
      const connection = await connectWallet(wallet);
      const { data: challenge } = await platformFetch<{ data: { nonce: string } }>("wallets/nonce", {
        type: scheme,
      });

      const message = signInMessage({
        chain: wallet.chain,
        domain: window.location.host,
        address: connection.address,
        uri: window.location.origin,
        statement: ADD_WALLET_STATEMENT,
        chainId: connection.chainId,
        nonce: challenge.nonce,
        issuedAt: new Date().toISOString(),
      });

      const signature = await signWalletMessage(wallet, connection.address, message);

      try {
        return (
          await platformSend<{ data: { id: string; address: string } }>("wallets", {
            type: scheme,
            message,
            signature,
          })
        ).data;
      } catch (error) {
        if (error instanceof PlatformError && error.status === 409) {
          return null;
        }

        throw error;
      }
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["wallets"] });
    },
  });
};
